import { useEffect } from 'react';

export interface UseSensorToggleRegistrationProps {
  registerSensorToggleHandler?: (dataLayer: string, handler: (sectionId: string, sensorName: string) => void) => void;
  dataLayer: string | undefined;
  enabledSensors: Record<string, Record<string, boolean>>;
  disableSensor: (sectionType: string, sensorName: string) => void;
  handleSensorAddition: (sectionType: string, sensorName: string) => void;
}

/**
 * Registers sensor toggle handler for external components (like search filters)
 * Allows external components to enable/disable sensors by toggling their current state
 */
export function useSensorToggleRegistration({
  registerSensorToggleHandler,
  dataLayer,
  enabledSensors,
  disableSensor,
  handleSensorAddition,
}: UseSensorToggleRegistrationProps): void {
  useEffect(() => {
    if (registerSensorToggleHandler && dataLayer) {
      // Create a handler that actually toggles based on current state
      const toggleHandler = (sectionId: string, sensorName: string) => {
        // Check if the sensor is currently enabled in this section
        const isCurrentlyEnabled = enabledSensors[sectionId]?.[sensorName] === true;

        if (isCurrentlyEnabled) {
          // Sensor is enabled -> disable it
          disableSensor(sectionId, sensorName);
        } else {
          // Sensor is disabled -> enable it
          handleSensorAddition(sectionId, sensorName);
        }
      };
      registerSensorToggleHandler(dataLayer, toggleHandler);
    }
  }, [registerSensorToggleHandler, dataLayer, handleSensorAddition, enabledSensors, disableSensor]);
}
